import { InputTextField } from '@lastbrand/web-ui/input-text-field'
import { useTranslation } from 'next-i18next'

import { TEST_LOCATORS } from '@src/constants/test-locators'
import { ProductCTAConstants } from '@src/quince-components/src/atoms/productCTA/constants'
import type { ProductWaitlistCTAType } from '@src/quince-components/src/atoms/productCTA/types'
import useATCVsATBExperiment from '@src/quince-site-common/src/hooks/use-atc-vs-atb-experiment'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'

import {
  buttonWrapperColumnCss,
  buttonWrapperCss,
  leavingSoonTextCss,
  pointerEventsNoneCss,
  quickATCWrapperCss,
  waitlistInputCss,
  waitlistedButtonCss,
  waitlistedButtonNewDesignV2Css,
} from './product-cta-ds.styles'
import { ProductDisplayButtonDS } from './product-display-button-ds'
import { ProductDisplayTextDS, RenderErrorMessageDS } from './product-message-ds'

import type { ChangeEvent } from 'react'

/**
 * DS twin of legacy `ProductWaitlistCTA`. Covers:
 *   - post-order: disabled OUT_OF_STOCK button
 *   - leaving-soon: ITEM_NO_LONGER_AVAILABLE + SHOP_SIMILAR (or ADD_TO_BAG/CART label in quick-ATC modal)
 *   - default: OOS + back-in-stock date, email input (logged-out + not joined), JOIN_WAITLIST / WAITLISTED
 * Email entry is the web-ui `InputTextField`; validation + onJoinWaitList live in the parent `handleClick`.
 */
export const ProductWaitlistCTADS = (props: ProductWaitlistCTAType) => {
  const { t } = useTranslation('common')
  const showATB = useATCVsATBExperiment()
  const atcLabel = showATB ? ProductCTAConstants.ADD_TO_BAG : ProductCTAConstants.ADD_TO_CART
  const {
    selectedVariantPurchaseInfo,
    isCurrentProductOptionLeavingSoon,
    isPostOrderFlow,
    isQuickATCForMobile,
    trackIsVisibleWaitlistRef,
    email,
    setEmail,
    setIsKeyboardVisibleForQuickATCForMobile,
    handleClick,
    errorMessage,
    isQuickATCModal,
  } = props

  const hasJoinedWaitList = selectedVariantPurchaseInfo?.hasJoinedWaitList

  if (isPostOrderFlow) {
    return (
      <ProductDisplayButtonDS onClick={() => undefined} disabled buttonCss={pointerEventsNoneCss}>
        {t(ProductCTAConstants.OUT_OF_STOCK)}
      </ProductDisplayButtonDS>
    )
  }

  if (isCurrentProductOptionLeavingSoon) {
    return (
      <div className={buttonWrapperCss}>
        <div className={leavingSoonTextCss}>{t(ProductCTAConstants.ITEM_NO_LONGER_AVAILABLE)}</div>
        <ProductDisplayButtonDS onClick={handleClick} buttonCss={waitlistedButtonCss} disabled={isQuickATCModal}>
          {isQuickATCModal ? t(atcLabel) : t(ProductCTAConstants.SHOP_SIMILAR)}
        </ProductDisplayButtonDS>
      </div>
    )
  }

  const showEmailInput = !hasJoinedWaitList && !AuthUtil.isLoggedIn()
  const placeholder = t('enter_your_email_address')

  // V2 black/white button only on the default (desktop/PDP) path; quick-ATC-mobile keeps cantaloupe.
  const useV2WaitlistButton = !isQuickATCForMobile

  const waitlistButton = hasJoinedWaitList ? (
    <ProductDisplayButtonDS
      onClick={handleClick}
      buttonCss={useV2WaitlistButton ? waitlistedButtonNewDesignV2Css : waitlistedButtonCss}
    >
      {t(ProductCTAConstants.WAITLISTED)}
    </ProductDisplayButtonDS>
  ) : (
    <ProductDisplayButtonDS
      onClick={handleClick}
      buttonCss={useV2WaitlistButton ? waitlistedButtonNewDesignV2Css : undefined}
    >
      {t(ProductCTAConstants.JOIN_WAITLIST)}
    </ProductDisplayButtonDS>
  )

  return (
    <div data-testid={TEST_LOCATORS.PRODUCT_CTA} ref={trackIsVisibleWaitlistRef} className={buttonWrapperColumnCss}>
      <ProductDisplayTextDS
        date={selectedVariantPurchaseInfo?.backInStockDate}
        isQuickATCForMobile={isQuickATCForMobile}
      />
      {showEmailInput ? (
        <InputTextField
          css={waitlistInputCss}
          value={email ?? ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail?.(e.target.value)}
          onFocus={() => setIsKeyboardVisibleForQuickATCForMobile?.(true)}
          onBlur={() => setIsKeyboardVisibleForQuickATCForMobile?.(false)}
          labelPlacement="outside"
          placeholder={placeholder}
          aria-label={placeholder}
          type="email"
        />
      ) : null}
      {!hasJoinedWaitList && errorMessage ? (
        <RenderErrorMessageDS errorMessage={errorMessage} isQuickATCForMobile={isQuickATCForMobile} />
      ) : null}
      {isQuickATCForMobile ? <div className={quickATCWrapperCss}>{waitlistButton}</div> : waitlistButton}
    </div>
  )
}
